import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../Utils/baseQueryWithReauth";
import {
    ICustomerPerceptionInsightsResponse,
    ICustomerPerceptionCohortResponse
} from "../components/CustomerPerception/CustomerPerception.interfaces";

/**
 * API slice for Customer Perception Insights functionality
 * 
 * Endpoints:
 * - getCustomerPerceptionInsights: Fetches main dashboard data (KPIs, Sankey, chips, funnel)
 * - getCustomerPerceptionCohort: Fetches cohort-specific data when a stage is selected
 */
export const customerPerceptionAPI = createApi({
    reducerPath: "customerPerception",
    tagTypes: ["CustomerPerception", "CustomerPerceptionCohort"],
    baseQuery: baseQueryWithReauth,
    // Cache data for 1 hour - data updates daily
    keepUnusedDataFor: 3600,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
    endpoints: (builder) => ({
        /**
         * Fetches main Customer Perception Insights data.
         * Includes KPI tiles, Sankey nodes/links, customer chips, and funnel cards.
         */
        getCustomerPerceptionInsights: builder.query<ICustomerPerceptionInsightsResponse, void>({
            query: () => ({
                url: "cvc-customer/perception-insights",
                method: "GET"
            }),
            providesTags: ["CustomerPerception"],
            keepUnusedDataFor: 3600
        }),

        /**
         * Fetches cohort-specific data for Journey Board drill-down.
         * Called when user selects a pipeline stage.
         */
        getCustomerPerceptionCohort: builder.query<ICustomerPerceptionCohortResponse, string>({
            query: (selectedStage) => ({
                url: "cvc-customer/perception-insights/cohort",
                method: "GET",
                params: { selectedStage }
            }),
            providesTags: (_result, _error, selectedStage) => [
                { type: "CustomerPerceptionCohort", id: selectedStage }
            ],
            keepUnusedDataFor: 3600
        })
    })
});

export const {
    useGetCustomerPerceptionInsightsQuery,
    useGetCustomerPerceptionCohortQuery,
    useLazyGetCustomerPerceptionCohortQuery
} = customerPerceptionAPI;
